import React, { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { format } from "date-fns";
import { MoreVertical, Folder, Calendar, Trash2 } from "react-feather";
import {
  Link as StyledLink,
  Icon,
  Flex,
  Text,
  Box,
  Checkbox,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  FormLabel,
  Tooltip,
  FlexProps,
  BoxProps,
} from "@chakra-ui/react";
import { useBreakpointValue } from "@chakra-ui/media-query";
import getConfig from "next/config";
import FileSaver from "file-saver";
import { useDraggable } from "@dnd-kit/core";
import { CSS } from "@dnd-kit/utilities";

import { useToastPromise } from "@/hooks/useToast";
import PreviewModal from "./PreviewModal";
import BoxCard from "@/components/common/BoxCard";
import { CloudType, File } from "@/types/cloud";
import FileTag from "@/components/common/FileTag";
import { formatBytes } from "@/utils/filesize";
import { Image } from "@/components/common/Image";
import { useUser } from "@/hooks/useUser";

export const FileCard = ({
  file,
  onDelete,
  onClick,
  onEdit,
  onSelectTag,
  hideCheckbox = false,
  hideDetails = false,
  preview = false,
  isChecked,
  onCheckClick,
  ...props
}: FileProps & BoxProps): React.ReactElement => {
  const { t } = useTranslation("common");
  const size = useBreakpointValue({ base: "sm", md: "md" });
  const { id, name, type, extension, filesize, tags } = file;
  const { publicRuntimeConfig } = getConfig();
  const hostName =
    typeof window !== "undefined" &&
    `${window.location.protocol}//${window.location.host}${publicRuntimeConfig.basePath}`;
  const toastPromise = useToastPromise();
  const [session] = useUser();
  const { attributes, listeners, setNodeRef, transform } = useDraggable({
    id: `draggableFile_${id}_${name}`,
    data: file,
  });

  const style = {
    transform: CSS.Translate.toString(transform),
  };

  const onTagClick = useCallback(
    (tagId) => (e) => {
      e.stopPropagation();
      onSelectTag(tagId);
    },
    []
  );

  const saveFile = () => {
    FileSaver.saveAs(
      `${publicRuntimeConfig.basePath}/cloud/file/${file?.filename}`,
      file?.name
    );
  };

  return (
    <Box
      minH={{ xl: "15vw", md: "none", base: "none" }}
      minW={{ xl: "14vw", md: "none", base: "none" }}
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      transform={style.transform}
    >
      <BoxCard
        h="100%"
        p={0}
        textAlign="center"
        cursor="pointer"
        transitionTimingFunction="cubic-bezier(.4,0,.2,1)"
        transitionDuration=".3s"
        transitionProperty="transform,box-shadow"
        _hover={{
          transform: { base: "none", md: "scale(1.05)" },
          boxShadow: {
            base: "none",
            md: "0 0 transparent, 0 0 transparent, 0 20px 25px -5px rgba(0,0,0,0.1), 0 10px 10px -5px rgba(0,0,0,0.04)",
          },
        }}
        pos="relative"
        display="flex"
        flexDirection="column"
        alignItems="stretch"
        {...props}
      >
        <Flex
          p={3}
          pb={0}
          align="center"
          justify={hideCheckbox ? "flex-end" : "space-between"}
        >
          {!hideCheckbox && (
            <Checkbox
              size="sm"
              isChecked={isChecked}
              onChange={(e) => onCheckClick(e, file)}
            />
          )}
          {!hideDetails && (
            <>
              {(onEdit || onDelete) && (
                <Menu placement="bottom-end">
                  <MenuButton role="group">
                    <Icon as={MoreVertical} />
                  </MenuButton>
                  <MenuList>
                    {onEdit && (
                      <MenuItem onClick={onEdit}>{t("edit")}</MenuItem>
                    )}
                    {onDelete && (
                      <MenuItem onClick={() => onDelete(id)}>
                        {t("delete")}
                      </MenuItem>
                    )}
                  </MenuList>
                </Menu>
              )}
            </>
          )}
          {preview && (
            <Menu placement="bottom-end">
              <MenuButton role="group">
                <Icon as={MoreVertical} />
              </MenuButton>
              <MenuList>
                <MenuItem
                  onClick={() => {
                    toastPromise.promise(
                      navigator.clipboard.writeText(
                        `${hostName}/cloud/preview/${file?.filename}`
                      ),
                      {
                        success: t("copyToClipboardSuccess"),
                        error: t("copyToClipboardError"),
                      }
                    );
                  }}
                >
                  {t("copyToClipboard")}
                </MenuItem>
                <MenuItem
                  opacity={session?.user?.id ? 1 : 0.3}
                  cursor={session?.user?.id ? "pointer" : null}
                  onClick={session?.user?.id ? saveFile : null}
                >
                  {session?.user?.id
                    ? t("cloud.download")
                    : t("cloud.loginToDownload")}
                </MenuItem>
              </MenuList>
            </Menu>
          )}
        </Flex>
        <Box mb={5} p={5} pt={0} onClick={onClick}>
          <Box
            w="60%"
            position="relative"
            borderRadius=".375rem"
            overflow="hidden"
            mx="auto"
            _before={{
              content: "''",
              pt: "100%",
              d: "block",
              width: "100%",
            }}
          >
            {type !== CloudType.PHOTO && (
              <>
                <Image priority src={`/file.svg`} objectFit="contain" alt="" />
                <Text
                  color="#FFFFFF"
                  position="absolute"
                  top="50%"
                  left="15%"
                  right="15%"
                  textTransform="uppercase"
                  isTruncated
                >
                  {extension}
                </Text>
              </>
            )}
            {type === CloudType.PHOTO && (
              <Image
                priority
                src={`/cloud/file/${file?.filename}`}
                objectFit="cover"
                alt=""
                quality={50}
              />
            )}
          </Box>
          <Text fontWeight="500" mt={4} isTruncated>
            {name}
          </Text>
          <Text color="text.500" mt=".125rem" fontSize="xs">
            {formatBytes(filesize)}
          </Text>
        </Box>
        <Flex
          pos="absolute"
          left="0"
          right="0"
          top="100%"
          mt="-2.2rem"
          justify="center"
          wrap="wrap"
          mr={-1}
          ml={tags?.length > 3 ? "-29px" : -1}
        >
          {tags?.map((tag) => (
            <Box
              mr={{ base: "-19px", md: tags?.length > 3 ? "-25px" : 1 }}
              key={tag?.id}
            >
              <FileTag
                name={tag?.name}
                hideName
                colorScheme={tag?.tagGroup?.colorScheme}
                picture={tag?.picture}
                size={size}
                onClick={onTagClick(tag?.id)}
              />
            </Box>
          ))}
        </Flex>
      </BoxCard>
    </Box>
  );
};

type FileProps = {
  file: File;
  onDelete?: (id) => void;
  onClick: () => void;
  onDoubleClick?: () => void;
  onEdit?: () => void;
  onSelectTag?: (id: number) => void;
  onCheckClick?: (e: React.ChangeEvent<HTMLInputElement>, file: File) => void;
  hideCheckbox?: boolean;
  isSelected?: boolean;
  isChecked?: boolean;
  hideDetails?: boolean;
  preview?: boolean;
};

export const FileRow = ({
  file,
  onDelete,
  onClick,
  onDoubleClick,
  onEdit,
  onSelectTag,
  onCheckClick,
  hideCheckbox = true,
  isSelected,
  isChecked,
  hideDetails,
  ...props
}: FileProps & FlexProps): React.ReactElement => {
  const { t } = useTranslation("common");
  const { id, createdAt, name, type, extension, filesize, tags } = file;

  const onTagClick = useCallback(
    (tagId) => (e) => {
      e.stopPropagation();
      onSelectTag(tagId);
    },
    []
  );

  return (
    <Flex
      mx={1}
      px={3}
      py={1}
      align="center"
      justify="space-between"
      cursor="pointer"
      borderRadius=".375rem"
      _hover={!isSelected && { bg: "light.100" }}
      transitionProperty="all"
      transitionDuration=".3s"
      transitionTimingFunction="cubic-bezier(.4,0,1,1)"
      bg={isSelected && "main.800"}
      color={isSelected && "#FFFFFF"}
      {...props}
    >
      {!hideCheckbox && (
        <Checkbox
          size="sm"
          isChecked={isChecked}
          onChange={(e) => onCheckClick(e, file)}
        />
      )}
      <Flex
        flex="1"
        pl={hideCheckbox ? undefined : 5}
        pr={5}
        align="center"
        onClick={onClick}
        onDoubleClick={onDoubleClick}
        maxW={hideCheckbox ? "calc(100% - 14px)" : "calc(100% - 32px)"}
      >
        <Box
          w="40px"
          position="relative"
          borderRadius=".375rem"
          overflow="hidden"
          mr={3}
          _before={{
            content: "''",
            pt: "100%",
            d: "block",
            width: "100%",
          }}
        >
          {type !== CloudType.PHOTO && (
            <>
              <Image priority src={`/file.svg`} objectFit="contain" alt="" />
              {type !== CloudType.PHOTO && extension?.length <= 3 && (
                <Text
                  color="#FFFFFF"
                  pos="absolute"
                  left="0"
                  right="0"
                  top="50%"
                  mt="-10px"
                  textTransform="uppercase"
                  fontSize="xx-small"
                >
                  {extension}
                </Text>
              )}
            </>
          )}
          {type === CloudType.PHOTO && (
            <Image
              priority
              src={`/cloud/file/${id}`}
              objectFit="cover"
              alt=""
            />
          )}
        </Box>
        <Text fontWeight="500" flex="1" textAlign="left" isTruncated>
          {name}
        </Text>
        {tags.map((tag) => (
          <Box
            ml={{ base: "-25px", md: tags?.length > 3 ? "-19px" : 0 }}
            key={tag?.id}
          >
            <FileTag
              name={tag?.name}
              hideName
              colorScheme={tag?.tagGroup?.colorScheme}
              picture={tag?.picture}
              size="sm"
              onClick={onTagClick(tag?.id)}
            />
          </Box>
        ))}
        {file?.folder && (
          <Flex ml={3} align="center">
            <Icon as={Folder} />
            <Text ml={3} fontSize="xs">
              {file?.folder?.parentFolderId
                ? file?.folder?.name
                : t("cloud.rootFolder")}
            </Text>
          </Flex>
        )}
        {!hideDetails && (
          <>
            <Flex ml={3} align="center">
              <Icon as={Calendar} />
              <Text ml={3} fontSize="xs" w="65px" textAlign="left">
                {format(new Date(createdAt), "dd/MM/yyyy")}
              </Text>
            </Flex>
            <Text color="text.500" align="right" w="60px" ml={3} fontSize="xs">
              {formatBytes(filesize)}
            </Text>
          </>
        )}
      </Flex>
      {onEdit && onDelete && (
        <Menu placement="bottom-end">
          <MenuButton role="group">
            <Icon as={MoreVertical} />
          </MenuButton>
          <MenuList>
            {onEdit && <MenuItem onClick={onEdit}>{t("edit")}</MenuItem>}
            {onDelete && (
              <MenuItem onClick={() => onDelete(id)}>{t("delete")}</MenuItem>
            )}
          </MenuList>
        </Menu>
      )}
    </Flex>
  );
};

export const FormFileList = ({
  files,
  onFileRemove,
}: {
  files: File[];
  onFileRemove: (id: number) => void;
}): JSX.Element => {
  const { t } = useTranslation("common");
  const [selectedFile, setSelectedFile] = useState(null);

  return (
    <>
      {(files || [])?.map((file) => (
        <Flex key={file.id} maxW="100%">
          <FormLabel
            mr={2}
            onClick={() => setSelectedFile(file)}
            cursor="pointer"
          >
            <Text noOfLines={1}>{file.name}</Text>
          </FormLabel>
          <Tooltip hasArrow label={t("delete")} placement="top">
            <StyledLink
              lineHeight="1"
              p="1"
              onClick={() => onFileRemove(file.id)}
            >
              <Icon as={Trash2} />
            </StyledLink>
          </Tooltip>
        </Flex>
      ))}

      {selectedFile && (
        <PreviewModal
          file={selectedFile}
          isOpen
          onClose={() => setSelectedFile(null)}
        />
      )}
    </>
  );
};
